/**
 * ParentPaymentMethods.js
 * Allows a parent to save / replace / remove their credit card via Stripe.
 * Card data goes directly to Stripe — never to our backend.
 *
 * Requires REACT_APP_STRIPE_PUBLISHABLE_KEY in the build environment.
 */
import React, { useState, useEffect, useRef } from "react";
import API_BASE from "./apiConfig";
import { useTheme, getBrandColors} from "./ThemeContext";

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";

const CARD_LOGOS = {
  visa:       "VISA",
  mastercard: "MC",
  amex:       "AMEX",
  discover:   "DISC",
  jcb:        "JCB",
  unionpay:   "UP",
};

export default function ParentPaymentMethods({ currentUser }) {
  const { isDark } = useTheme();
  const txt  = isDark ? "#e2e8f0" : "#1a2840";
  const dim  = isDark ? "rgba(255,255,255,0.45)" : "rgba(3,78,123,0.55)";
  const acc  = isDark ? getBrandColors().orange : getBrandColors().blue;

  const cardSt = {
    background:   isDark ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.88)",
    border:       isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,53,128,0.10)",
    borderRadius: 16, padding: 24, backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
  };

  const [savedCard,   setSavedCard]   = useState(null);  // {brand, last4, exp_month, exp_year}
  const [loadingCard, setLoadingCard] = useState(true);
  const [showForm,    setShowForm]    = useState(false);
  const [processing,  setProcessing]  = useState(false);
  const [removing,    setRemoving]    = useState(false);
  const [msg,         setMsg]         = useState(null);

  // Stripe instances
  const stripeRef  = useRef(null);
  const cardElRef  = useRef(null);
  const mountedRef = useRef(false);

  const parentId = currentUser?.parent_id;
  const email    = currentUser?.email  || "";
  const name     = currentUser?.full_name || currentUser?.name || "";

  // ── Load saved card info ───────────────────────────────────────────────────
  useEffect(() => {
    if (!parentId) { setLoadingCard(false); return; }
    setLoadingCard(true);
    fetch(`${API_BASE}/oto/parent-payment-method/${parentId}`)
      .then(r => r.json())
      .then(d => setSavedCard(d.card || null))
      .catch(() => setSavedCard(null))
      .finally(() => setLoadingCard(false));
  }, [parentId]);

  // ── Mount Stripe Elements card form ───────────────────────────────────────
  useEffect(() => {
    if (!showForm || !STRIPE_PK || mountedRef.current) return;

    let cancelled = false;

    import("@stripe/stripe-js").then(({ loadStripe }) =>
      loadStripe(STRIPE_PK)
    ).then(stripe => {
      if (cancelled || !stripe) return;
      stripeRef.current = stripe;

      const elements = stripe.elements();
      const card = elements.create("card", {
        style: {
          base: {
            fontSize: "15px",
            color: isDark ? "#e2e8f0" : "#1a2840",
            "::placeholder": { color: isDark ? "rgba(255,255,255,0.35)" : "rgba(3,78,123,0.40)" },
          },
        },
      });

      const el = document.getElementById("parent-card-element");
      if (el) {
        card.mount("#parent-card-element");
        cardElRef.current = card;
        mountedRef.current = true;
      }
    });

    return () => { cancelled = true; };
  }, [showForm, isDark]);

  const openForm = () => {
    mountedRef.current = false;
    cardElRef.current  = null;
    setMsg(null);
    setShowForm(true);
  };

  const closeForm = () => {
    if (cardElRef.current) { try { cardElRef.current.unmount(); } catch {} }
    cardElRef.current  = null;
    mountedRef.current = false;
    setShowForm(false);
    setMsg(null);
  };

  // ── Save card flow ─────────────────────────────────────────────────────────
  const handleSaveCard = async () => {
    if (!stripeRef.current || !cardElRef.current) {
      setMsg({ ok: false, text: "Card form not ready. Please wait a moment." }); return;
    }
    setProcessing(true); setMsg(null);

    try {
      // Step 1 — get SetupIntent client_secret from our backend
      const siRes  = await fetch(`${API_BASE}/oto/parent-setup-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ parent_id: parentId, email, name }),
      });
      const siData = await siRes.json();
      if (!siData.success) throw new Error(siData.message || "Could not initialise payment setup");

      // Step 2 — confirm card directly with Stripe (card data never leaves the browser)
      const { setupIntent, error } = await stripeRef.current.confirmCardSetup(
        siData.client_secret,
        { payment_method: { card: cardElRef.current, billing_details: { name, email } } }
      );
      if (error) throw new Error(error.message);

      // Step 3 — tell our backend which pm_xxx was confirmed (no card data)
      const saveRes  = await fetch(`${API_BASE}/oto/parent-save-payment-method`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ parent_id: parentId, payment_method_id: setupIntent.payment_method }),
      });
      const saveData = await saveRes.json();
      if (!saveData.success) throw new Error(saveData.message || "Could not save card");

      setSavedCard(saveData.card);
      setMsg({ ok: true, text: "Card saved successfully." });
      closeForm();

    } catch (e) {
      setMsg({ ok: false, text: e.message || "Something went wrong." });
    }
    setProcessing(false);
  };

  // ── Remove card ────────────────────────────────────────────────────────────
  const handleRemove = async () => {
    if (!window.confirm("Remove your saved card?")) return;
    setRemoving(true);
    try {
      await fetch(`${API_BASE}/oto/parent-payment-method/${parentId}`, { method: "DELETE" });
      setSavedCard(null);
      setMsg({ ok: true, text: "Card removed." });
    } catch {
      setMsg({ ok: false, text: "Could not remove card." });
    }
    setRemoving(false);
  };

  if (!STRIPE_PK) {
    return (
      <div style={{ ...cardSt, maxWidth: 500, color: "#92400e",
        background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.30)" }}>
        <strong>Stripe not configured.</strong> The publishable key is missing.
        Add <code>REACT_APP_STRIPE_PUBLISHABLE_KEY</code> to your environment and rebuild.
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 520 }}>
      <h3 style={{ margin: "0 0 6px", fontSize: 17, fontWeight: 800, color: acc }}>Payment Method</h3>
      <p style={{ margin: "0 0 18px", fontSize: 13, color: dim }}>
        Your card is stored securely with Stripe — we never see your card number.
      </p>

      {loadingCard ? (
        <div style={{ ...cardSt, opacity: 0.6, fontSize: 13 }}>Loading…</div>
      ) : savedCard ? (
        <div style={{ ...cardSt, display: "flex", alignItems: "center", gap: 16 }}>
          {/* Card logo */}
          <div style={{
            width: 52, height: 34, borderRadius: 6, background: "#1a2840",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 10, fontWeight: 800, color: "#fff", letterSpacing: 0.5,
          }}>
            {CARD_LOGOS[savedCard.brand?.toLowerCase()] || savedCard.brand?.toUpperCase()}
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: txt }}>
              •••• •••• •••• {savedCard.last4}
            </div>
            <div style={{ fontSize: 12, color: dim, marginTop: 2 }}>
              Expires {String(savedCard.exp_month).padStart(2,"0")}/{String(savedCard.exp_year).slice(-2)}
            </div>
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={openForm} style={{
              padding: "7px 14px", borderRadius: 8, border: "none", cursor: "pointer",
              background: isDark ? "rgba(255,255,255,0.10)" : "rgba(0,53,128,0.09)",
              color: txt, fontWeight: 600, fontSize: 12,
            }}>Replace</button>
            <button onClick={handleRemove} disabled={removing} style={{
              padding: "7px 14px", borderRadius: 8, border: "none", cursor: "pointer",
              background: "rgba(239,68,68,0.10)", color: getBrandColors().red, fontWeight: 600, fontSize: 12,
            }}>{removing ? "Removing…" : "Remove"}</button>
          </div>
        </div>
      ) : (
        !showForm && (
          <div style={{ ...cardSt, textAlign: "center" }}>
            <div style={{ fontSize: 13, color: dim, marginBottom: 16 }}>No card on file</div>
            <button onClick={openForm} style={{
              padding: "10px 28px", borderRadius: 9, border: "none", cursor: "pointer",
              background: "#f59e0b", color: "#fff", fontWeight: 700, fontSize: 14,
            }}>+ Add Card</button>
          </div>
        )
      )}

      {/* ── Stripe Elements card form ── */}
      {showForm && (
        <div style={{ ...cardSt, marginTop: savedCard ? 16 : 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: txt, marginBottom: 14 }}>
            {savedCard ? "Replace Card" : "Add Card"}
          </div>

          <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase",
            letterSpacing: "0.06em", color: dim, display: "block", marginBottom: 8 }}>
            Card Details
          </label>

          {/* Stripe injects the card input here — no card data in our DOM */}
          <div id="parent-card-element" style={{
            padding: "12px 14px", borderRadius: 9,
            background: isDark ? "rgba(255,255,255,0.06)" : "#f4f7fb",
            border: isDark ? "1.5px solid rgba(255,255,255,0.14)" : "1.5px solid rgba(0,53,128,0.18)",
            minHeight: 44,
          }} />

          <div style={{ fontSize: 11, color: dim, marginTop: 8, marginBottom: 18 }}>
            🔒 Encrypted and handled by Stripe. Your card number is never sent to Sixers servers.
          </div>

          {msg && (
            <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 14, fontSize: 13,
              background: msg.ok ? "rgba(16,185,129,0.12)" : "rgba(239,68,68,0.12)",
              color: msg.ok ? getBrandColors().green : getBrandColors().red }}>{msg.text}</div>
          )}

          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={handleSaveCard} disabled={processing} style={{
              flex: 1, padding: "11px 0", borderRadius: 9, border: "none", cursor: "pointer",
              background: processing ? "rgba(245,158,11,0.5)" : "#f59e0b",
              color: "#fff", fontWeight: 700, fontSize: 14,
            }}>{processing ? "Saving…" : "Save Card"}</button>
            <button onClick={closeForm} disabled={processing} style={{
              padding: "11px 20px", borderRadius: 9, cursor: "pointer",
              border: "1.5px solid rgba(0,53,128,0.20)", background: "transparent",
              color: txt, fontWeight: 600, fontSize: 14,
            }}>Cancel</button>
          </div>
        </div>
      )}

      {msg && !showForm && (
        <div style={{ padding: "10px 14px", borderRadius: 8, marginTop: 12, fontSize: 13,
          background: msg.ok ? "rgba(16,185,129,0.12)" : "rgba(239,68,68,0.12)",
          color: msg.ok ? getBrandColors().green : getBrandColors().red }}>{msg.text}</div>
      )}
    </div>
  );
}
